import { PlusCircleOutlined } from '@ant-design/icons';
import { Button, Col, Row, Space } from 'antd';
import { useFormikContext } from 'formik';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { type TextCarouselWebPageBlockInput, WebPageBlockType } from '../../../../api/types';
import CollapsibleWrapper, { Panel } from '../../../../components/wrappers/CollapsibleWrapper';
import FormFields from '../../../../themes/admin/Fields/FormFields';
import useSystemOptions from '../../../../utilities/useSystemOptions';
import { type WebpageFormikValue } from '../shared';
import { colSpan } from './ColumnListBlock';
import ReorderingButtons, { getNewBlock } from './shared';
import TextCarouselDescription from './TextCarouselDescription';

type TextCarouselBlockProps = {
    name: string;
    index: number;
    disabled?: boolean;
};

const TextCarouselBlock = ({ name, index, disabled = false }: TextCarouselBlockProps) => {
    const { t } = useTranslation(['webpageDetails']);
    const { values, setFieldValue } = useFormikContext<WebpageFormikValue>();

    const onSelect = useCallback(
        (value, options) => {
            values.webPageBlock[index] = getNewBlock(value);
            setFieldValue(name, getNewBlock(value));
        },
        [index, name, setFieldValue, values.webPageBlock]
    );

    const addDescriptions = useCallback(() => {
        const textCarouselBlock = values.webPageBlock[index] as TextCarouselWebPageBlockInput;
        if (values.webPageBlock[index].type !== WebPageBlockType.TextCarousel) {
            throw new Error('WebPageBlockType is not same as input');
        }

        setFieldValue(`${name}.descriptions`, [...textCarouselBlock.descriptions, { defaultValue: '', overrides: [] }]);
    }, [values.webPageBlock, index, setFieldValue, name]);

    const { webpageBlockType } = useSystemOptions();

    return (
        <Space direction="vertical" style={{ width: '100%' }}>
            <CollapsibleWrapper defaultActiveKey={t('webpageDetails:fields.textCarousel.blockName')}>
                <Panel
                    key={t('webpageDetails:fields.textCarousel.blockName')}
                    extra={
                        !disabled && <ReorderingButtons index={index} setFieldValue={setFieldValue} values={values} />
                    }
                    header={t('webpageDetails:fields.textCarousel.blockName')}
                >
                    <Row gutter={14}>
                        <Col {...colSpan}>
                            <FormFields.SelectField
                                {...t('webpageDetails:fields.blockType', { returnObjects: true })}
                                disabled={disabled}
                                name={`${name}.type`}
                                onSelect={onSelect}
                                options={webpageBlockType}
                                showSearch
                            />
                        </Col>
                        <Col {...colSpan}>
                            <FormFields.TranslatedInputField
                                {...t('webpageDetails:fields.textCarousel.title', { returnObjects: true })}
                                disabled={disabled}
                                name={`${name}.title`}
                            />
                        </Col>
                    </Row>
                    <TextCarouselDescription disabled={disabled} name={`${name}.descriptions`} />
                    {!disabled && (
                        <Button icon={<PlusCircleOutlined />} onClick={addDescriptions} type="link">
                            {t('webpageDetails:actions.add')}
                        </Button>
                    )}
                </Panel>
            </CollapsibleWrapper>
        </Space>
    );
};

export default TextCarouselBlock;
